import { useCallback, useRef, useEffect } from 'react';

export function useDebounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
) {
  const timerRef = useRef<number | null>(null);

  const debouncedFunc = useCallback(
    (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
      return new Promise((resolve) => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(async () => {
          const result = await func(...args);
          resolve(result);
        }, delay);
      });
    },
    [func, delay],
  );

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  return { debouncedFunc, cancel };
}
